import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEmployeeService } from "../../services/EmployeeService";
import dayjs from "dayjs";
import AssignLocationForm from "./AssignLocationForm";

const columnDefs = [
  { key: "LocationName", label: "Location", flex: 1.5 },
  { key: "Latitude", label: "Latitude", flex: 1 },
  { key: "Longitude", label: "Longitude", flex: 1 },
  // { key: "LocationType", label: "Type", flex: 1 },
  { key: "GeoRadius", label: "Radius (km)", flex: 1 },
  { key: "AssignedDate", label: "Assigned Date", flex: 1 },
  { key: "actions", label: "", flex: 0.5 },
];

const EmployeeLocationList = ({ empID, onDelete }) => {
  const { fetchAssignedLocations, deleteAssignedLocation } =
    useEmployeeService();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const all = await fetchAssignedLocations();
        const filtered = all.filter((item) => item.EmployeeId === empID);
        setData(filtered);
      } catch (err) {
        console.error("Failed to fetch assigned locations:", err);
      } finally {
        setLoading(false);
      }
    };

    if (empID) fetch();
  }, [empID, refreshTrigger]);

  const handleDelete = async () => {
    try {
      await deleteAssignedLocation({ mappingId: selectedRow.MappingId });
      setData((prev) =>
        prev.filter((row) => row.MappingId !== selectedRow.MappingId)
      );
      setConfirmOpen(false);
      await fetchAssignedLocations({ EmployeeId: "All" }, true);
    } catch (err) {
      console.error("Failed to delete assignment:", err);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6" gutterBottom>
          {showForm ? "Assign New Location" : "Assigned Locations"}
        </Typography>

        {!showForm && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowForm(true)}
          >
            Assign Location
          </Button>
        )}
      </Box>

      {!showForm && <Divider sx={{ mb: 1 }} />}

      {showForm && (
        <Box sx={{ mb: 2 }}>
          <AssignLocationForm
            empID={empID}
            onSubmit={() => {
              setShowForm(false);
              setRefreshTrigger((prev) => prev + 1); // This will trigger useEffect again
            }}
            onClose={() => setShowForm(false)}
          />
        </Box>
      )}

      {loading ? (
        <LinearProgress />
      ) : data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No assigned locations.
        </Typography>
      ) : (
        <Box component={Paper}>
          <Box
            sx={{ display: "flex", p: 1, fontWeight: 600, fontSize: "0.85rem" }}
          >
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1 }}>
                {label}
              </Box>
            ))}
          </Box>

          {data.map((row) => (
            <Box
              key={row.MappingId}
              sx={{
                display: "flex",
                alignItems: "center",
                borderTop: "1px solid #eee",
                px: 1,
                py: 0.5,
                fontSize: "0.8rem",
              }}
            >
              {columnDefs.map(({ key, flex }) => (
                <Box key={key} sx={{ flex, pr: 1 }}>
                  {key === "actions" ? (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedRow(row);
                          setConfirmOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : key === "AssignedDate" ? (
                    <>
                      {dayjs(row[key]?.replace(/Z$/, "")).format(
                        "MMM DD, YYYY"
                      )}
                    </>
                  ) : (    
                    row[key] ?? "-"
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this location assignment?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeLocationList;
